import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Users, Ticket, Plane, CalendarCheck, Clock, Bell,
  UserPlus, CalendarDays,
  ArrowRight, Search as SearchIcon, UserCircle2,
  FileText, CreditCard, Users2, TrendingUp, Plus,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { DisplayName } from "@/lib/name";

/* ------------------ Stat Card ------------------ */
function StatCard({ icon: Icon, label, value, tint, onClick, testid }) {
  const tints = {
    blue: { bg: "bg-blue-100", fg: "text-[#0b3a82]" },
    indigo: { bg: "bg-indigo-100", fg: "text-indigo-700" },
    sky: { bg: "bg-sky-100", fg: "text-sky-700" },
    violet: { bg: "bg-violet-100", fg: "text-violet-700" },
    emerald: { bg: "bg-emerald-100", fg: "text-emerald-700" },
    amber: { bg: "bg-amber-100", fg: "text-amber-700" },
    rose: { bg: "bg-rose-100", fg: "text-rose-700" },
  };
  const t = tints[tint] || tints.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      className="soft-card p-5 text-left w-full hover:-translate-y-0.5 transition"
      data-testid={testid}
    >
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl grid place-items-center ${t.bg} ${t.fg}`}>
          <Icon className="w-6 h-6" strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="text-3xl font-bold text-slate-900 leading-tight">{value ?? "—"}</div>
        </div>
      </div>
      <div className="mt-3 text-xs text-[#0b3a82] font-medium flex items-center gap-1">
        Ver detalhes <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </button>
  );
}

/* ------------------ Row block ------------------ */
function SectionCard({ title, icon: Icon, action, actionLabel, children, testid }) {
  return (
    <div className="soft-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-slate-900">
          {Icon && <Icon className="w-4 h-4 text-[#0b3a82]" />}
          <h3 className="font-semibold">{title}</h3>
        </div>
        {action && (
          <button
            type="button"
            onClick={action}
            className="text-xs font-semibold text-[#0b3a82] hover:underline"
          >
            {actionLabel || "Ver todos"}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/* ------------------ Passenger avatar ------------------ */
function Avatar({ initials, tint = "blue" }) {
  const tints = {
    blue: "bg-blue-100 text-[#0b3a82]",
    orange: "bg-orange-100 text-orange-700",
    violet: "bg-violet-100 text-violet-700",
    emerald: "bg-emerald-100 text-emerald-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-bold ${tints[tint] || tints.blue}`}>
      {initials || "?"}
    </div>
  );
}

/* ------------------ Main HomeOverview ------------------ */
export default function HomeOverview({ onNavigate, onOpenCheckins, onQuickAction }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/home");
        setData(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const s = data?.stats || {};
  const flights = data?.upcoming_flights || [];
  const checkins = data?.checkins_48h || [];
  const alerts = data?.alerts || [];
  const chart = data?.chart_30d || [];
  const agendaToday = data?.agenda_today || [];

  return (
    <div className="space-y-5" data-testid="home-overview">
      {/* Prominent quick create buttons */}
      <div className="flex flex-wrap items-center gap-3" data-testid="home-quick-create">
        <Button
          onClick={() => onQuickAction?.("new-emission")}
          className="h-11 px-5 orange-grad text-white hover:opacity-95 font-semibold shadow-lg shadow-orange-500/20"
          data-testid="home-new-emission-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nova emissão
        </Button>
        <Button
          onClick={() => onQuickAction?.("new-client")}
          className="h-11 px-5 orange-grad text-white hover:opacity-95 font-semibold shadow-lg shadow-orange-500/20"
          data-testid="home-new-client-btn"
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> Cadastrar cliente
        </Button>
      </div>

      {/* Row 1: 6 stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <StatCard icon={Users} label="Clientes" value={s.clients} tint="blue"
          onClick={() => onNavigate?.("clients")} testid="stat-clients" />
        <StatCard icon={UserPlus} label="Novos no mês" value={s.new_clients_month} tint="amber"
          onClick={() => onNavigate?.("clients")} testid="stat-new-clients-month" />
        <StatCard icon={Ticket} label="Emissões" value={s.emissions} tint="indigo"
          onClick={() => onNavigate?.("emissions")} testid="stat-emissions" />
        <StatCard icon={CalendarCheck} label="Check-ins pendentes" value={s.checkins_pending} tint="violet"
          onClick={onOpenCheckins} testid="stat-checkins" />
        <StatCard icon={Clock} label="Voos 7 dias" value={s.flights_7d} tint="emerald"
          onClick={() => onNavigate?.("emissions")} testid="stat-flights-7d" />
        <StatCard icon={TrendingUp} label="Voos até o fim do ano" value={s.flights_rest_year} tint="rose"
          onClick={() => onNavigate?.("emissions")} testid="stat-flights-year" />
      </div>

      {/* Row 2: Próximos voos | Check-ins 48h | Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Próximos voos */}
        <SectionCard title="Próximos voos" icon={Plane}
          action={() => onNavigate?.("emissions")} testid="upcoming-flights">
          {flights.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Nenhum voo próximo.</p>
          ) : (
            <ul className="space-y-3">
              {flights.map((f) => (
                <li key={f.emission_id} className="flex items-center gap-3">
                  <Avatar initials={f.initials} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      <DisplayName name={f.passenger_name} />
                      {f.passenger_count > 1 && (
                        <span className="ml-1.5 text-xs text-slate-500 font-normal">
                          +{f.passenger_count - 1} ({f.passenger_count} pax)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {f.origin} <ArrowRight className="inline w-3 h-3 mx-0.5" /> {f.destination}
                      <span className="mx-1.5 text-slate-300">•</span>
                      {f.when_label}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Check-ins próximas 48h */}
        <SectionCard title="Check-ins próximas 48h" icon={Plane}
          action={onOpenCheckins} testid="checkins-48h">
          {checkins.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Nenhum check-in em 48h.</p>
          ) : (
            <ul className="space-y-3">
              {checkins.map((f) => (
                <li key={f.emission_id} className="flex items-center gap-3">
                  <Avatar initials={f.initials} tint="orange" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      <DisplayName name={f.passenger_name} />
                      {f.passenger_count > 1 && (
                        <span className="ml-1.5 text-xs text-slate-500 font-normal">
                          +{f.passenger_count - 1} ({f.passenger_count} pax)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {f.airline} {f.flight_number}
                      <span className="mx-1.5 text-slate-300">•</span>
                      {f.when_label}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="border-blue-200 text-[#0b3a82] hover:bg-blue-50 shrink-0"
                    onClick={onOpenCheckins}
                    data-testid={`home-checkin-btn-${f.emission_id}`}>
                    <Plane className="w-3.5 h-3.5 mr-1" /> Fazer check-in
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Alertas importantes */}
        <SectionCard title="Alertas importantes" icon={Bell}
          action={onOpenCheckins} actionLabel="Ver todos os alertas →" testid="alerts">
          {alerts.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Tudo em ordem por aqui.</p>
          ) : (
            <ul className="space-y-3">
              {alerts.map((a, i) => {
                const iconMap = {
                  checkin: UserCircle2,
                  cpf: FileText,
                  payment: CreditCard,
                  boarding: Users2,
                  receivable: CreditCard,
                };
                const Ico = iconMap[a.type] || Bell;
                return (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-50 text-red-600 grid place-items-center shrink-0">
                      <Ico className="w-4 h-4" />
                    </div>
                    <div className="text-sm text-slate-700">{a.text}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* Row 3: Chart | Agenda */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <div className="lg:col-span-2 soft-card p-5" data-testid="chart-30d">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-slate-900">Emissões</h3>
              <p className="text-xs text-slate-500">últimos 30 dias</p>
            </div>
            <button type="button" onClick={() => onNavigate?.("finance")}
              className="text-xs font-semibold text-[#0b3a82] hover:underline">
              Ver relatório →
            </button>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }}
                  interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                  formatter={(v) => [`${v}`, "Emissões"]}
                />
                <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2.5}
                  dot={{ r: 3, fill: "#2563eb" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Agenda de hoje */}
        <SectionCard title="Agenda de hoje" icon={CalendarDays}
          action={() => onNavigate?.("agenda")} testid="agenda-today">
          {agendaToday.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Sem compromissos hoje.</p>
          ) : (
            <ul className="space-y-3 text-sm">
              {agendaToday.map((it) => (
                <li key={it.agenda_id} className="flex items-start gap-3">
                  <div className="text-xs font-bold text-[#0b3a82] w-12 shrink-0 pt-0.5">
                    {it.time_label || "—"}
                  </div>
                  <div className={`flex-1 ${it.completed ? "text-slate-400 line-through" : "text-slate-700"}`}>
                    {it.title}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Próximos embarques */}
      </div>

      {/* Dica do dia bar */}
      <div className="soft-card p-4 flex items-center gap-3" data-testid="tip-of-day">
        <div className="w-9 h-9 rounded-full bg-blue-100 text-[#0b3a82] grid place-items-center shrink-0">
          <SearchIcon className="w-4 h-4" />
        </div>
        <p className="text-sm text-slate-700">
          <b>Dica do dia:</b> Mantenha os dados dos seus clientes sempre atualizados para uma melhor experiência!
        </p>
      </div>
    </div>
  );
}
